import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Collapse,
  Container,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  fetchPatientNotesList,
  fetchNotesByAppointmentId,
  getSessionById,
  deleteSessionById,
  updateSessionById,
  getAppointmentById,
} from "../../api";
import { FollowUpSession } from "./FollowUpSession";
import { UpdateSession } from "./EditSession";
import {
  ADD_OPTION_CLASS,
  AddNoteExpander,
  contentKindsOf,
  deriveKind,
  expandNoteRows,
  iconForKind,
  isLegacyBundle,
  noteTitleOf,
} from "./SessionNoteRail";
import SyncToButton, { hasSyncToAccess } from "../SyncTo/SyncToButton";
import { useRecording } from "../SyncTo/RecordingProvider";
import CaptureReviewBar from "../SyncTo/CaptureReviewBar";
import ConfirmModal from "../Confirm/ConfirmModal";
import { useConfirm } from "../Confirm/useConfirm";
import NoteAuditModal from "./NoteAuditModal";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import "../Accounts/acc-list.css";
import "./EditSession.css";

/**
 * The patient's notes: one list, grouped by sitting, beside one editor.
 *
 * This replaces two sections that used to host the same editor twice:
 * "Appointment Notes" (only the current appointment's notes) and "Previous
 * Notes" (every other visit, one at a time, behind a dropdown). Both are the
 * same thing seen from different ends, and keeping them apart meant the two
 * drifted (media photos showed on one and not the other, delete confirmed on
 * one and not the other) and that the current visit never appeared beside the
 * history it belongs to.
 *
 * The shape mirrors the mobile app's patient file:
 *   - every note the patient has, newest sitting first, collapsed to one row
 *     per sitting (an appointment, or a group of ad-hoc notes written in one
 *     sitting);
 *   - when the file was opened from an appointment, that sitting is pinned at
 *     the top and open;
 *   - every open sitting ends with its own Add row, so where a note will go
 *     is where the button is. With no appointment pinned, a dashed "ad hoc
 *     note" box at the top starts a fresh sitting dated today, so an ad-hoc
 *     note is one click away without a separate screen.
 *
 * The rows come from the light, paginated list endpoint (no note bodies), so
 * a long history costs nothing until a note is opened. The current
 * appointment's notes are fetched separately and merged, so an old
 * appointment's sitting is complete even when it falls outside the first
 * page.
 *
 * Unregistered bookings (a New Patient appointment with no patient record yet)
 * use the same component with `patientId` "1": the list is just that
 * appointment's notes, and SyncTo and the invoice hand-off are off.
 */

const PAGE_SIZE = 30;
const DRAFT_ID = "__draft__";
/** The always-present target for starting a fresh ad-hoc sitting. */
const GHOST_KEY = "adhoc:new";

const newGroupId = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
  `adhoc-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDay = (iso) => {
  if (!iso) return "Unknown date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const stampOf = (r) =>
  new Date(r.sessionsAppointmentStart || r.createdAt || r.updatedAt || 0).getTime();

const sittingKeyOf = (row) => {
  const isAdHoc = !!row.sessionsAdHoc || !row.sessionsAppointmentId;
  return isAdHoc
    ? `adhoc:${row.sessionsAdHocGroupId || row._id}`
    : `appt:${row.sessionsAppointmentId}`;
};

/** Rows from two fetches, deduplicated by id. */
const mergeRows = (a, b) => {
  const seen = new Set();
  const out = [];
  for (const r of [...(a || []), ...(b || [])]) {
    const id = String(r._id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
};

/**
 * Collapse the flat rows into sittings. The current appointment (if any) is
 * pinned first even when it has no notes yet; with no appointment, the ghost
 * "new ad hoc" target leads instead; the rest run newest first.
 */
function buildSittings(rows, { appointmentId, appointment, service, registered }) {
  const byKey = new Map();
  for (const r of rows) {
    const isAdHoc = !!r.sessionsAdHoc || !r.sessionsAppointmentId;
    const key = sittingKeyOf(r);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        isAdHoc,
        appointmentId: isAdHoc ? null : String(r.sessionsAppointmentId),
        adHocGroupId: isAdHoc ? r.sessionsAdHocGroupId || null : null,
        serviceName: r.sessionsServiceName || "",
        when: r.sessionsAppointmentStart || r.createdAt || null,
        practiceId: r.sessionsPracticeId || null,
        resourceId: r.sessionsResourceId || null,
        status: r.sessionsAppointmentStatus || null,
        notes: [],
      });
    }
    const g = byKey.get(key);
    g.notes.push(r);
    // The sitting's time is its earliest note: the appointment's own time.
    if (!g.when || stampOf(r) < new Date(g.when).getTime()) {
      g.when = r.sessionsAppointmentStart || r.createdAt || g.when;
    }
    if (!g.practiceId && r.sessionsPracticeId) g.practiceId = r.sessionsPracticeId;
    if (!g.resourceId && r.sessionsResourceId) g.resourceId = r.sessionsResourceId;
  }

  let pinned = null;
  if (appointmentId) {
    const key = `appt:${appointmentId}`;
    pinned = byKey.get(key) || {
      key,
      isAdHoc: false,
      appointmentId: String(appointmentId),
      adHocGroupId: null,
      serviceName: "",
      when: null,
      practiceId: null,
      resourceId: null,
      status: null,
      notes: [],
    };
    byKey.delete(key);
    pinned.isCurrent = true;
    pinned.serviceName = service?.servicesName || pinned.serviceName;
    pinned.when = appointment?.start || pinned.when;
    pinned.practiceId = appointment?.practiceId || pinned.practiceId;
    pinned.resourceId = appointment?.resourceId || pinned.resourceId;
    pinned.status = appointment?.status || pinned.status;
    pinned.isInvoiced = !!appointment?.isInvoiced;
    pinned.bookingNote = typeof appointment?.notes === "string" ? appointment.notes.trim() : "";
  }

  const rest = [...byKey.values()];
  for (const g of rest) g.notes.sort((a, b) => stampOf(a) - stampOf(b));
  if (pinned) pinned.notes.sort((a, b) => stampOf(a) - stampOf(b));
  rest.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));

  const out = [];
  if (pinned) out.push(pinned);
  // The "new ad hoc sitting" target only when there is no appointment to
  // file into. On an appointment it was one more thing to read past, and the
  // appointment is where the notes made today belong.
  if (registered && !pinned) {
    out.push({ key: GHOST_KEY, isAdHoc: true, isGhost: true, notes: [], appointmentId: null, adHocGroupId: null });
  }
  return [...out, ...rest];
}

export default function PatientNotes({
  patientId,
  appointmentId = null,
  appointment = null,
  service = null,
  patientLabel = "",
  // The practice this file is being worked under, for a recording or an
  // ad-hoc note with nothing else to take it from.
  practiceId = null,
  // Ask to open a specific note (AI History's "Open note"). The token changes
  // on every request so the same id can be asked for twice; the owner clears
  // the request through onOpenNoteHandled once it has been acted on, so a
  // later remount does not replay it.
  openNoteId = null,
  openNoteToken = 0,
  onOpenNoteHandled,
  // Called with the updated appointment after this section changes it
  // (completing it), so the owner's copy stays in step.
  onAppointmentChanged,
}) {
  const registered = !!patientId && String(patientId) !== "1";
  const effectivePatientId = registered ? patientId : "1";
  const recording = useRecording();
  const { confirm, confirmProps } = useConfirm();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState([]);
  const rowsRef = useRef([]);
  const [total, setTotal] = useState(0);
  // How far into the server's paginated list we have read. Kept apart from
  // rows.length because rows also holds the pinned appointment's notes and
  // any note opened by id, which may sit beyond the pages fetched so far;
  // paging by rows.length skipped the notes in between.
  const [serverSkip, setServerSkip] = useState(0);
  const serverSkipRef = useRef(0);
  // Request sequencing: a load or an open that resolves after a newer one
  // started must not overwrite it (switching patient, clicking two notes).
  const loadSeqRef = useRef(0);
  const openSeqRef = useRef(0);
  const [loading, setLoading] = useState(true);
  // Set when the list fetch failed, so a 500 reads as "could not load" with
  // a retry rather than as a patient with no notes.
  const [loadError, setLoadError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  // The sitting Add files into.
  const [selectedKey, setSelectedKey] = useState(null);
  // The open note: its list row id (a legacy bundle has one row per kind, all
  // sharing a document), the full document, and the kind being shown.
  const [activeRowId, setActiveRowId] = useState(null);
  const [activeNote, setActiveNote] = useState(null);
  const [activeNoteKind, setActiveNoteKind] = useState(null);
  const [draftNoteKind, setDraftNoteKind] = useState(null);
  // Bumped on every Add, so a second note of the same kind still gets a fresh
  // editor instead of React reusing the last one with its content and id.
  const [draftToken, setDraftToken] = useState(0);
  // Stamped onto an ad-hoc sitting the moment a note is added to it, so the
  // draft joins it before the refreshed list arrives.
  const [pendingAdHocGroupId, setPendingAdHocGroupId] = useState(null);
  // Bumped only when something outside the editor rewrites the open note (a
  // SyncTo apply). The editor's own saves must not remount it.
  const [remountToken, setRemountToken] = useState(0);
  const editorRef = useRef(null);
  const initialisedRef = useRef(false);
  // Below lg the list stacks above the editor. Opening or adding a note then
  // brings the editor into view, so the user is not left looking at the list
  // wondering where the note went.
  const editorColRef = useRef(null);
  const revealEditorOnSmallScreens = () => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 991.98px)").matches) return;
    window.requestAnimationFrame(() => {
      editorColRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  // Mirrors draftNoteKind for handlers that need to know, after an await,
  // whether a draft is still open.
  const draftOpenRef = useRef(false);
  useEffect(() => {
    draftOpenRef.current = !!draftNoteKind;
  }, [draftNoteKind]);

  /**
   * saveNow() returns null both for "nothing to save" and for "the save
   * failed", and the two must not be treated alike: discarding a draft whose
   * save failed would throw the user's typing away.
   */
  const openNoteHasUnsaved = () => {
    const probe = editorRef.current?.hasUnsavedContent;
    return typeof probe === "function" ? !!probe() : false;
  };

  const setRowsBoth = (next) => {
    rowsRef.current = next;
    setRows(next);
  };
  const setServerSkipBoth = (next) => {
    serverSkipRef.current = next;
    setServerSkip(next);
  };

  const sittings = useMemo(
    () => buildSittings(rows, { appointmentId, appointment, service, registered }),
    [rows, appointmentId, appointment, service, registered],
  );
  const target = sittings.find((s) => s.key === selectedKey) || null;
  const pinned = sittings.find((s) => s.isCurrent) || null;

  // ── data ──────────────────────────────────────────────────────────────────

  /** Returns the merged rows plus how many came off the paginated list. */
  const fetchRows = useCallback(async (count) => {
    if (!registered) {
      if (!appointmentId) return { notes: [], total: 0, pageCount: 0 };
      const notes = await fetchNotesByAppointmentId(appointmentId);
      return {
        notes: notes.map((n) => ({ ...n, sessionsAppointmentId: String(appointmentId) })),
        total: notes.length,
        pageCount: notes.length,
      };
    }
    const [page, apptNotes] = await Promise.all([
      fetchPatientNotesList(patientId, { limit: count, skip: 0 }),
      appointmentId ? fetchNotesByAppointmentId(appointmentId) : Promise.resolve([]),
    ]);
    const stamped = (apptNotes || []).map((n) => ({
      ...n,
      sessionsAppointmentId: String(appointmentId),
    }));
    return {
      notes: mergeRows(page.notes, stamped),
      total: page.total,
      pageCount: page.notes.length,
    };
  }, [registered, patientId, appointmentId]);

  const openRow = useCallback(async (row) => {
    if (!row?._id) return;
    const seq = ++openSeqRef.current;
    setDraftNoteKind(null);
    setActiveRowId(String(row._id));
    setActiveNoteKind(row.sessionType || null);
    const docId = row.realId || row._id;
    try {
      const full = await getSessionById(docId);
      // A later click wins: do not put this document under that row.
      if (seq !== openSeqRef.current) return;
      if (full && full._id) setActiveNote(full);
    } catch (err) {
      if (seq !== openSeqRef.current) return;
      console.error("Could not open this note:", err);
      showToast("error", "Could not open this note. Please try again.");
    }
  }, [showToast]);

  /**
   * Open a note by id when it is not among the loaded rows (AI History can
   * point at one beyond the pages fetched so far). The document is placed in
   * its sitting and merged into the rows; the server offset is untouched so
   * paging stays correct. A note from another patient is refused: the
   * request is only ever meant for the file on screen.
   */
  const openById = useCallback(async (id, { quiet = false } = {}) => {
    const seq = ++openSeqRef.current;
    try {
      const full = await getSessionById(String(id));
      if (seq !== openSeqRef.current) return false;
      if (!full?._id) return false;
      // Only this file's notes may open here: the patient's, or for an
      // unregistered booking, this appointment's.
      const foreign = registered
        ? !!full.sessionsPatientId && String(full.sessionsPatientId) !== String(patientId)
        : String(full.sessionsAppointmentId || "") !== String(appointmentId || "");
      if (foreign) {
        if (!quiet) showToast("error", "That note belongs to a different patient.");
        return false;
      }
      const key = sittingKeyOf(full);
      setSelectedKey(key);
      expandKey(key);
      setDraftNoteKind(null);
      setActiveRowId(String(full._id));
      setActiveNoteKind(deriveKind(full));
      setActiveNote(full);
      // A light row, like the list endpoint's: the body stays in activeNote.
      const row = {
        _id: String(full._id),
        sessionType: deriveKind(full),
        sessionsNoteTitle: noteTitleOf(full),
        contentKinds: contentKindsOf(full),
        legacyBundle: isLegacyBundle(full),
        sessionsTemplateId: full.sessionsTemplateId || null,
        sessionsServiceName: full.sessionsServiceName || "",
        sessionsAppointmentId: full.sessionsAppointmentId || null,
        sessionsAdHoc: !!full.sessionsAdHoc,
        sessionsAdHocGroupId: full.sessionsAdHocGroupId || null,
        sessionsAppointmentStart: full.sessionsAppointmentStart || null,
        sessionsPracticeId: full.sessionsPracticeId ?? null,
        sessionsResourceId: full.sessionsResourceId || null,
        createdAt: full.createdAt || null,
        updatedAt: full.updatedAt || null,
        // Survives list refreshes that do not cover it (see load).
        __openedById: true,
      };
      setRowsBoth(mergeRows(rowsRef.current, [row]));
      return true;
    } catch (err) {
      if (seq !== openSeqRef.current) return false;
      console.error("Could not open the requested note:", err);
      if (!quiet) showToast("error", "That note could not be opened.");
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registered, patientId, appointmentId, showToast]);

  // One sitting open at a time: opening one closes the others, so the list
  // reads as an accordion and the highlight is always on the sitting whose
  // notes are showing.
  const expandKey = useCallback((key) => {
    setExpanded((prev) => (prev.size === 1 && prev.has(key) ? prev : new Set([key])));
  }, []);

  /**
   * Load (or reload) the rows and decide what is open.
   *
   * `preferNoteId` keeps the user on the note they were working on across a
   * refresh. With `reopen: false` the list refreshes and the editor is left
   * alone, which is what a save needs: replacing the editor's props mid-edit
   * is what made every save look like a page reload.
   */
  const load = useCallback(async (preferNoteId, { reopen = true } = {}) => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const count = Math.min(100, Math.max(PAGE_SIZE, serverSkipRef.current));
      const { notes, total: t, pageCount } = await fetchRows(count);
      // The patient changed (or a newer load started) while this was in
      // flight: its rows belong to a list that is no longer on screen.
      if (seq !== loadSeqRef.current) return rowsRef.current;
      setLoadError(false);
      // Keep any note opened by id that the pages do not cover. Everything
      // below reads the merged list, so such a note is found like any other.
      const kept = rowsRef.current.filter(
        (r) => r.__openedById && !notes.some((n) => String(n._id) === String(r._id)),
      );
      const merged = mergeRows(notes, kept);
      setRowsBoth(merged);
      setTotal(t);
      setServerSkipBoth(pageCount);
      const firstLoad = !initialisedRef.current;
      initialisedRef.current = true;
      if (!reopen) return merged;

      const built = buildSittings(merged, { appointmentId, appointment, service, registered });
      const findRowFor = (id) => {
        for (const g of built) {
          const n = g.notes.find((x) => String(x._id) === String(id));
          if (n) return { sitting: g, row: expandNoteRows(n)[0] };
        }
        return null;
      };

      if (preferNoteId) {
        const hit = findRowFor(preferNoteId);
        if (hit) {
          setSelectedKey(hit.sitting.key);
          expandKey(hit.sitting.key);
          await openRow(hit.row);
          return merged;
        }
        // Asked for by id but not in the loaded pages (AI History pointing
        // deep into the history, or a note just added to an old sitting the
        // pages do not reach): fetch it directly. A note that has gone falls
        // through, quietly, to the normal selection.
        if (await openById(preferNoteId, { quiet: !firstLoad })) return merged;
      }

      if (firstLoad) {
        const current = built.find((g) => g.isCurrent);
        if (current) {
          // Opened from an appointment: land in it, on its newest note.
          setSelectedKey(current.key);
          expandKey(current.key);
          const last = current.notes[current.notes.length - 1];
          if (last) await openRow(expandNoteRows(last)[0]);
          return merged;
        }
        const newest = built.find((g) => !g.isGhost);
        if (newest) {
          setSelectedKey(newest.key);
          expandKey(newest.key);
          const last = newest.notes[newest.notes.length - 1];
          if (last) await openRow(expandNoteRows(last)[0]);
          return merged;
        }
        // Nothing to read yet: Add goes straight into a fresh sitting.
        setSelectedKey(registered ? GHOST_KEY : null);
        return merged;
      }

      // A later reload with nothing asked for: keep the selection, but drop
      // an open note that no longer exists.
      if (activeRowId && activeRowId !== DRAFT_ID) {
        const stillThere = merged.some(
          (n) => String(n._id) === String(activeRowId).split("#")[0],
        );
        if (!stillThere) {
          setActiveRowId(null);
          setActiveNote(null);
          setActiveNoteKind(null);
        }
      }
      return merged;
    } catch (err) {
      if (seq !== loadSeqRef.current) return rowsRef.current;
      console.error("Error loading notes:", err);
      setLoadError(true);
      return rowsRef.current;
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRows, appointmentId, appointment, service, registered, activeRowId, openRow, openById, expandKey]);

  // A new patient or appointment: start over. Bumping the sequences also
  // discards whatever an earlier load or open still has in flight.
  useEffect(() => {
    loadSeqRef.current += 1;
    openSeqRef.current += 1;
    initialisedRef.current = false;
    rowsRef.current = [];
    setRows([]);
    setServerSkipBoth(0);
    setTotal(0);
    setLoadError(false);
    setActiveRowId(null);
    setActiveNote(null);
    setActiveNoteKind(null);
    setDraftNoteKind(null);
    setSelectedKey(null);
    setExpanded(new Set());
    // A pending open-note request (AI History) takes the place of the
    // default selection, so the two cannot race each other.
    const requested = openNoteId && openNoteToken ? String(openNoteId) : undefined;
    load(requested).finally(() => {
      if (requested) onOpenNoteHandled?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, appointmentId]);

  // The pinned sitting's label and practice arrive with the appointment,
  // which may load after the first fetch; no refetch is needed for that since
  // buildSittings reads it live.

  const loadMore = useCallback(async () => {
    if (!registered || loadingMore) return;
    setLoadingMore(true);
    // Same guard as load(): a page that arrives after the patient changed
    // belongs to the previous patient's list and must not be merged.
    const seq = loadSeqRef.current;
    try {
      const skip = serverSkipRef.current;
      const page = await fetchPatientNotesList(patientId, { limit: PAGE_SIZE, skip });
      if (seq !== loadSeqRef.current) return;
      setRowsBoth(mergeRows(rowsRef.current, page.notes));
      setTotal(page.total);
      setServerSkipBoth(skip + page.notes.length);
    } catch (err) {
      console.error("Error loading more notes:", err);
      showToast("error", "Could not load more notes.");
    } finally {
      setLoadingMore(false);
    }
  }, [registered, loadingMore, patientId, showToast]);

  // ── open-by-id requests (AI History's "Open note") ───────────────────────

  useEffect(() => {
    if (!openNoteId || !openNoteToken) return;
    // On a fresh mount the initial load takes the request (see above).
    if (!initialisedRef.current) return;
    (async () => {
      const id = String(openNoteId);
      const savedId = await saveOpenNote();
      if (savedId) await load(undefined, { reopen: false });
      const row = rowsRef.current.find((r) => String(r._id) === id);
      if (row) {
        const key = sittingKeyOf(row);
        setSelectedKey(key);
        expandKey(key);
        await openRow(expandNoteRows(row)[0]);
      } else {
        await openById(id);
      }
      onOpenNoteHandled?.();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNoteId, openNoteToken]);

  // ── handlers ─────────────────────────────────────────────────────────────

  /**
   * Save whatever note is open, if it has anything in it. Both editors expose
   * saveNow(); it no-ops on an untouched note, so an abandoned Add cannot leave
   * an empty one behind, and nothing is lost by moving between notes.
   */
  const saveOpenNote = useCallback(async () => {
    const save = editorRef.current?.saveNow;
    if (typeof save !== "function") return null;
    try {
      return await save();
    } catch (err) {
      console.error("Could not save the open note before switching:", err);
      return null;
    }
  }, []);

  /**
   * Close whatever is open in the editor (a saved note or a draft), saving
   * it first. The editor then shows its empty state.
   */
  const closeOpenNote = async () => {
    const savedId = await saveOpenNote();
    if (!savedId && openNoteHasUnsaved()) {
      showToast("error", "The open note could not be saved. Please try again.");
      return false;
    }
    if (savedId) await load(undefined, { reopen: false });
    setDraftNoteKind(null);
    setActiveRowId(null);
    setActiveNote(null);
    setActiveNoteKind(null);
    return true;
  };

  // Opening or closing a sitting also puts away the note that was open: the
  // editor follows the list, so a collapsed sitting never leaves one of its
  // notes standing alone on the right.
  const toggleSitting = async (g) => {
    if (!(await closeOpenNote())) return;
    if (g.isGhost) {
      setSelectedKey(GHOST_KEY);
      setExpanded(new Set());
      return;
    }
    const isOpen = expanded.has(g.key);
    setSelectedKey(g.key);
    setExpanded(isOpen && selectedKey === g.key ? new Set() : new Set([g.key]));
  };

  const handleSelectNote = async (row) => {
    if (!row?._id || row._id === DRAFT_ID) return;
    if (String(row._id) === String(activeRowId)) return;
    const savedId = await saveOpenNote();
    if (savedId) await load(undefined, { reopen: false });
    setSelectedKey(sittingKeyOf(row));
    await openRow(row);
    revealEditorOnSmallScreens();
  };

  /**
   * The ad-hoc group a new note should join. A loose ad-hoc note written before
   * sittings existed has no group, so one is stamped onto it now: that single
   * write is what lets an old standalone note grow into a sitting.
   */
  const resolveAdHocGroupId = async (g) => {
    if (!g?.isAdHoc) return null;
    // Always a fresh sitting. The pending id belongs to the draft that is
    // (or was) open under the ghost; reusing it would make the next ad-hoc
    // note join that one instead of starting its own.
    if (g.isGhost) return newGroupId();
    if (g.adHocGroupId) return g.adHocGroupId;
    const groupId = newGroupId();
    // All or nothing: a stamp that failed would leave the sitting keyed on
    // the old id while the draft files under the new one, which is the
    // "note landed in the wrong sitting" bug this exists to prevent.
    await Promise.all(
      (g.notes || []).map((n) => updateSessionById(n._id, { sessionsAdHocGroupId: groupId })),
    );
    return groupId;
  };

  /**
   * Start a new note of `kind` in `into` (the sitting whose Add row was
   * used). The note being left is saved first; nothing is written for the new
   * note until its editor saves, so an abandoned Add leaves nothing behind.
   */
  const handleAddNote = async (kind, into) => {
    const dest = into || target || pinned || sittings.find((s) => s.isGhost) || null;
    if (!dest) return;
    const savedId = await saveOpenNote();
    if (!savedId && openNoteHasUnsaved()) {
      showToast("error", "The open note could not be saved. Please try again.");
      return;
    }
    let groupId = null;
    try {
      groupId = await resolveAdHocGroupId(dest);
    } catch (err) {
      console.error("Could not group this ad-hoc note:", err);
      showToast("error", "Could not add to this sitting. Please try again.");
      return;
    }
    if (groupId) setPendingAdHocGroupId(groupId);
    const stamped = !!groupId && !dest.isGhost && !dest.adHocGroupId;
    if (stamped) {
      // Mirror the stamp locally before the reload. A row the pages do not
      // reach (opened by id, or past the reload cap) is carried across the
      // reload as it is, and it must carry the new group id or the regrouped
      // sitting would never appear and Add would fail every time.
      const ids = new Set((dest.notes || []).map((n) => String(n._id)));
      setRowsBoth(
        rowsRef.current.map((r) =>
          ids.has(String(r._id))
            ? { ...r, sessionsAdHocGroupId: groupId, __openedById: true }
            : r,
        ),
      );
    }
    if (savedId || stamped) {
      const fresh = await load(undefined, { reopen: false });
      // The reload must show the regrouped sitting before a draft is filed
      // into it; otherwise the draft would fall back to the pinned
      // appointment, which is exactly the wrong place.
      if (stamped && !fresh.some((r) => r.sessionsAdHocGroupId === groupId)) {
        showToast("error", "Could not add to this sitting. Please try again.");
        return;
      }
    }
    // A loose ad-hoc note that has just been given a group id is now keyed
    // on that group, so select the sitting by its new key, not the old one.
    const destKey = stamped ? `adhoc:${groupId}` : dest.key;
    setSelectedKey(destKey);
    expandKey(destKey);
    setDraftToken((t) => t + 1);
    setDraftNoteKind(kind);
    setActiveRowId(DRAFT_ID);
    setActiveNoteKind(kind);
    setActiveNote(null);
    revealEditorOnSmallScreens();
  };

  const handleDeleteNote = async (row) => {
    if (!row?._id || row.isSplit) return;
    if (row.isDraft || row._id === DRAFT_ID) {
      // Never saved: discarding it is purely local.
      setDraftNoteKind(null);
      setDraftToken((t) => t + 1);
      const g = target;
      const last = g?.notes?.[g.notes.length - 1];
      if (last) await openRow(expandNoteRows(last)[0]);
      else {
        setActiveRowId(null);
        setActiveNote(null);
        setActiveNoteKind(null);
      }
      return;
    }
    const ok = await confirm({
      title: "Delete note",
      message: `Delete "${row.sessionsNoteTitle}"? This cannot be undone.`,
      confirmLabel: "Delete",
      confirmVariant: "danger",
    });
    if (!ok) return;
    try {
      await deleteSessionById(row.realId || row._id);
    } catch (_err) {
      showToast("error", "Could not delete this note. Please try again.");
      return;
    }
    const deletedId = String(row.realId || row._id);
    const wasOpen = String(activeNote?._id || "") === deletedId;
    if (wasOpen) {
      setActiveRowId(null);
      setActiveNote(null);
      setActiveNoteKind(null);
    }
    // Drop it locally first: a row opened by id is kept across reloads that
    // do not cover it, and a deleted note must not be.
    setRowsBoth(rowsRef.current.filter((r) => String(r._id) !== deletedId));
    showToast("success", "Note deleted.");
    await load(undefined, { reopen: false });
  };

  /** The create editor saved. Implicit saves (stayPut) only refresh the list. */
  const handleDraftSaved = async (newId, opts) => {
    if (!newId) return;
    setPendingAdHocGroupId(null);
    if (opts?.stayPut) {
      await load(undefined, { reopen: false });
      return;
    }
    await load(newId);
  };

  // ── SyncTo ───────────────────────────────────────────────────────────────

  const jwtPracticeId = () => {
    try {
      const token = sessionStorage.getItem("user");
      const decoded = token ? jwtDecode(token) : null;
      if (Array.isArray(decoded?.practice) && decoded.practice.length > 0) {
        return String(decoded.practice[0]);
      }
    } catch (_err) { /* fall through */ }
    return null;
  };

  // An apply reaches us twice: RecordingProvider calls meta.onApplied and
  // also fires the window event. Handle the pair once.
  const lastAppliedRef = useRef({ id: null, at: 0 });
  const onSyncToApplied = useCallback(async ({ sessionId: appliedId } = {}) => {
    const id = String(appliedId || activeNote?._id || "");
    const now = Date.now();
    if (id && lastAppliedRef.current.id === id && now - lastAppliedRef.current.at < 1500) return;
    lastAppliedRef.current = { id, at: now };
    try {
      // The apply may land in a note other than the one being edited (the
      // user moved on while the worker ran). Opening that note unmounts the
      // editor, so persist its edits first.
      // A draft has no activeNote, so it counts as "another note" too;
      // saveNow() no-ops on an untouched draft, so this costs nothing.
      if (appliedId && String(activeNote?._id || "") !== String(appliedId)) {
        await saveOpenNote();
      }
      await load(appliedId || activeNote?._id);
      setRemountToken((t) => t + 1);
    } catch (_err) {
      // The write already happened server-side; a failed refresh is not fatal.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeNote?._id, saveOpenNote]);

  const onSyncToBeforeOpen = async (into) => {
    // Recording from another sitting's menu while a draft is open: the draft
    // is saved and left behind as a normal note (or discarded when empty), so
    // the transcript cannot land in it and the draft editor is not moved.
    const movingAway = !!into && into.key !== (target?.key || null);
    const leavingDraft = movingAway && !!draftNoteKind;
    // A recording must never cost unsaved edits: persist first, and record
    // against the note that is open so the transcript lands in it.
    const savedId = await saveOpenNote();
    if (!savedId && openNoteHasUnsaved()) {
      showToast("error", "The open note could not be saved. Please try again.");
      return { sessionId: null, error: new Error("Open note not saved") };
    }
    if (savedId) await load(savedId, { reopen: leavingDraft });
    if (leavingDraft) {
      if (!savedId) {
        // Truly empty: nothing to keep.
        setDraftNoteKind(null);
        setActiveRowId(null);
        setActiveNoteKind(null);
      } else if (draftOpenRef.current) {
        // The reopen did not land (a fetch failed), so the draft editor is
        // still up. Moving the selection now would remount it blank under
        // the other sitting; stop here instead. The note itself is saved.
        showToast("error", "Could not reopen the saved note. Please try again.");
        return { sessionId: null, error: new Error("Reopen failed") };
      }
    }
    const dest = into || target || pinned || null;
    if (into) setSelectedKey(into.key);
    const pid =
      dest?.practiceId ||
      appointment?.practiceId ||
      activeNote?.sessionsPracticeId ||
      practiceId ||
      jwtPracticeId();
    if (!pid) {
      showToast("error", "Could not resolve the practice for this recording. Please refresh and try again.");
      return { sessionId: null, error: new Error("Practice not resolved") };
    }
    // Record INTO the open note only when it belongs to the sitting the
    // caption names. A draft always files into the target, so one that was
    // just saved counts too. Otherwise the transcript becomes a new note in
    // that sitting, which is what the caption promised.
    const draftJustSaved = !!draftNoteKind && !!savedId && !movingAway;
    const noteInTarget =
      draftJustSaved ||
      (!!activeNote && !!dest && sittingKeyOf(activeNote) === dest.key);
    // The review step compares the transcript against what the note already
    // holds and asks before overwriting. A draft's fields live in the editor,
    // not in activeNote, so read them back from the note it was just saved
    // as; otherwise the review would see an empty note and overwrite them
    // without asking.
    let existingCapturedValues = {};
    if (noteInTarget) {
      if (draftJustSaved) {
        try {
          const saved = await getSessionById(savedId);
          existingCapturedValues = saved?.capturedValues || {};
        } catch (_err) {
          existingCapturedValues = {};
        }
      } else {
        existingCapturedValues = activeNote?.capturedValues || {};
      }
    }
    recording?.openModal({
      appointmentId: dest?.appointmentId || null,
      patientId,
      practiceId: pid,
      patientLabel,
      sessionId: noteInTarget ? savedId || String(activeNote._id) : null,
      existingCapturedValues,
      onApplied: onSyncToApplied,
    });
    // SyncToButton skips its own openModal when this returns a null sessionId.
    return { sessionId: null };
  };

  // Applying a capture writes a note, often a brand new one. Reload so it
  // appears, whichever surface the apply came from.
  useEffect(() => {
    const handler = (e) => {
      const detail = (e && e.detail) || {};
      // Only an apply for this patient, or into the note that is open, may
      // move this list; one with no patient at all is somebody else's.
      const forThisPatient = !!detail.patientId && String(detail.patientId) === String(patientId);
      const forOpenNote =
        !!detail.sessionId && !!activeNote?._id && String(detail.sessionId) === String(activeNote._id);
      if (!forThisPatient && !forOpenNote) return;
      onSyncToApplied({ sessionId: detail.sessionId });
    };
    window.addEventListener("syncto:applied", handler);
    return () => window.removeEventListener("syncto:applied", handler);
  }, [patientId, activeNote?._id, onSyncToApplied]);

  const syncToOn = registered && hasSyncToAccess();
  // SyncTo as the first entry of a sitting's Add menu. null rather than an
  // element that renders null: the menu draws its divider only when it
  // actually has a leading item.
  const syncToMenuItemFor = (g) =>
    syncToOn ? (
      <SyncToButton
        appointmentId={g?.appointmentId || null}
        patientLabel={patientLabel}
        sessionId={activeNote?._id ? String(activeNote._id) : null}
        onBeforeOpen={() => {
          setAddOpenKey(null);
          return onSyncToBeforeOpen(g);
        }}
        onApplied={onSyncToApplied}
        asListRow
        listRowClassName={ADD_OPTION_CLASS}
      />
    ) : null;

  // Which sitting's Add row is expanded into its options. One at a time.
  const [addOpenKey, setAddOpenKey] = useState(null);

  // The note whose audit trail is open in the audit modal. "Audit", not
  // "history": AI History is a different thing on the rail.
  const [auditFor, setAuditFor] = useState(null);

  // Appointment status per sitting, for the editor footer's "Complete
  // appointment" checkbox. The current appointment's comes with the
  // appointment itself; a previous appointment's is fetched once, when a
  // note of it is opened or added, and cached here. Until it is known the
  // checkbox stays hidden, so a completed visit is never offered again.
  const [apptStatus, setApptStatus] = useState({});
  const statusFor = (g) => {
    if (!g || !g.appointmentId) return null;
    if (g.isCurrent) return appointment?.status || null;
    return apptStatus[g.appointmentId]?.status ?? null;
  };
  const editedSitting = draftNoteKind
    ? target || pinned || null
    : activeNote
      ? sittings.find((s) => s.key === sittingKeyOf(activeNote)) || null
      : null;
  const statusWanted =
    editedSitting && editedSitting.appointmentId && !editedSitting.isCurrent
      ? editedSitting.appointmentId
      : null;
  useEffect(() => {
    if (!statusWanted || statusWanted in apptStatus) return;
    let cancelled = false;
    getAppointmentById(statusWanted)
      .then((a) => {
        if (cancelled) return;
        setApptStatus((prev) => ({
          ...prev,
          [statusWanted]: { status: a?.status || null, isInvoiced: !!a?.isInvoiced },
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setApptStatus((prev) => ({ ...prev, [statusWanted]: { status: null, isInvoiced: false } }));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusWanted]);

  // The editors' footer checkbox completes the appointment on save and
  // reports back through this, so the strip, the pinned sitting and the
  // cache follow and the checkbox goes away.
  const markAppointmentCompletedFor = (g) => () => {
    if (!g?.appointmentId) return;
    if (g.isCurrent) {
      if (appointment) onAppointmentChanged?.({ ...appointment, status: "Completed" });
      return;
    }
    setApptStatus((prev) => ({
      ...prev,
      [g.appointmentId]: { ...(prev[g.appointmentId] || {}), status: "Completed" },
    }));
  };

  // ── render helpers ───────────────────────────────────────────────────────

  const rowsFor = (g) => {
    const out = g.notes.flatMap((n) => expandNoteRows(n));
    if (draftNoteKind && g.key === (target?.key || null)) {
      out.push({
        _id: DRAFT_ID,
        sessionType: draftNoteKind,
        sessionsNoteTitle: "New note",
        isDraft: true,
      });
    }
    return out;
  };

  const sittingSubtitle = (g) => {
    const parts = [];
    if (g.isCurrent) parts.push(formatWhen(g.when));
    else parts.push(g.isAdHoc ? "Ad hoc" : formatTime(g.when));
    if (g.serviceName && g.serviceName !== "Ad Hoc") parts.push(g.serviceName);
    return parts.filter(Boolean).join(" · ");
  };

  /** The last row of an open sitting: add a note here. */
  const renderAddRow = (g, label) => (
    <div className="acc-notes-row acc-notes-add-row" key={`${g.key}#add`}>
      <AddNoteExpander
        label={label}
        id={`add-note-${g.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`}
        open={addOpenKey === g.key}
        onToggle={(next) => setAddOpenKey(next ? g.key : null)}
        onAdd={(kind) => {
          setAddOpenKey(null);
          handleAddNote(kind, g);
        }}
        leadingItems={syncToMenuItemFor(g)}
      />
    </div>
  );

  const renderRow = (g, row) => {
    const isActive = String(row._id) === String(activeRowId);
    return (
      <div className="acc-notes-row" key={row._id}>
        <button
          type="button"
          className={`acc-notes-row-btn${isActive ? " is-active" : ""}`}
          onClick={() => (row.isDraft ? null : handleSelectNote(row))}
          title={row.sessionsNoteTitle}
          aria-current={isActive ? "true" : undefined}
        >
          <i className={iconForKind(row.sessionType)} />
          <span className="acc-notes-row-title">{row.sessionsNoteTitle}</span>
          {row.isDraft && <span className="acc-notes-row-meta fst-italic">draft</span>}
        </button>
        {!row.isDraft && (
          <OverlayTrigger placement="top" overlay={<Tooltip>Audit trail</Tooltip>}>
            <Button
              variant="link"
              className="text-primary text-decoration-none p-0 acc-notes-row-icon"
              aria-label={`Audit trail of ${row.sessionsNoteTitle}`}
              onClick={() => setAuditFor({ id: String(row.realId || row._id), title: row.sessionsNoteTitle })}
            >
              <i className="fa-light fa-clock-rotate-left" style={{ fontSize: "0.8rem" }} />
            </Button>
          </OverlayTrigger>
        )}
        {!row.isSplit && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{row.isDraft ? "Discard draft" : "Delete note"}</Tooltip>}
          >
            <Button
              variant="link"
              className="text-danger text-decoration-none p-0 acc-notes-row-icon"
              aria-label={`${row.isDraft ? "Discard" : "Delete"} ${row.sessionsNoteTitle}`}
              onClick={() => handleDeleteNote(row)}
            >
              <i className="fa-solid fa-trash" style={{ fontSize: "0.75rem" }} />
            </Button>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const renderSitting = (g) => {
    const isSelected = selectedKey === g.key;
    const list = rowsFor(g);

    // The ghost is not a sitting yet, just the place to start one: a dashed
    // row holding the Add menu, with the draft beneath it once one exists.
    if (g.isGhost) {
      return (
        <div className={`acc-notes-sitting is-ghost${isSelected ? " is-selected" : ""}`} key={g.key}>
          <div className="acc-notes-rows border-0 pt-2">
            {list.map((row) => renderRow(g, row))}
            {renderAddRow(g, "Ad hoc note")}
          </div>
        </div>
      );
    }

    const isOpen = expanded.has(g.key);
    // The highlight means "this sitting's notes are showing": a collapsed
    // sitting is never highlighted, even if it is still Add's target.
    const classes = ["acc-notes-sitting", isSelected && isOpen ? "is-selected" : ""].filter(Boolean).join(" ");
    return (
      <div className={classes} key={g.key}>
        <button
          type="button"
          className={`acc-notes-sitting-head${isOpen ? "" : " is-collapsed"}`}
          onClick={() => toggleSitting(g)}
          aria-expanded={isOpen}
        >
          <span className={`acc-notes-sitting-chevron${isOpen ? " is-open" : ""}`}>
            <i className="fa-solid fa-chevron-right" />
          </span>
          <span className="acc-notes-sitting-title">
            <span className="acc-notes-sitting-date">
              {g.isCurrent ? "This appointment" : formatDay(g.when)}
            </span>
            <span className="acc-notes-sitting-sub">{sittingSubtitle(g)}</span>
          </span>
          {g.notes.length > 0 && (
            <Badge bg="light" text="dark" className="acc-notes-sitting-count">
              {g.notes.length}
            </Badge>
          )}
        </button>
        {/* Animated so the eye can follow one sitting closing as another
            opens; an instant swap read as nothing having happened. */}
        <Collapse in={isOpen} timeout={260}>
          <div>
            <div className="acc-notes-rows">
              {list.map((row) => renderRow(g, row))}
              {/* Add trails the notes, so where a note will go is where the
                  button is. */}
              {renderAddRow(g, "Add note")}
            </div>
          </div>
        </Collapse>
      </div>
    );
  };

  const canInvoiceFor = (g) =>
    registered && !!g?.isCurrent && !!g?.appointmentId && !g?.isInvoiced;

  const renderEditor = () => {
    if (draftNoteKind) {
      const dest = target || pinned;
      if (!dest) return null;
      return (
        <FollowUpSession
          key={`new-${dest.key}-${draftNoteKind}-${draftToken}`}
          ref={editorRef}
          adHoc={!!dest.isAdHoc}
          adHocGroupId={dest.adHocGroupId || pendingAdHocGroupId}
          appointmentId={dest.appointmentId || null}
          patientId={effectivePatientId}
          serviceName={dest.isAdHoc ? "" : dest.serviceName || ""}
          resourceId={dest.resourceId || null}
          appointmentStart={dest.isAdHoc ? null : dest.when}
          appointmentStatus={statusFor(dest)}
          practiceId={dest.practiceId || appointment?.practiceId || practiceId || undefined}
          sessionType={draftNoteKind}
          canInvoice={canInvoiceFor(dest)}
          onAppointmentCompleted={markAppointmentCompletedFor(dest)}
          onUpdate={handleDraftSaved}
        />
      );
    }
    if (activeNote) {
      const owner = sittings.find((s) => s.key === sittingKeyOf(activeNote)) || null;
      return (
        <UpdateSession
          key={`${activeNote._id}-${activeNoteKind || ""}-${remountToken}`}
          ref={editorRef}
          sessionId={activeNote._id}
          patientId={effectivePatientId}
          appointmentId={activeNote.sessionsAppointmentId || null}
          serviceName={activeNote.sessionsServiceName || owner?.serviceName || ""}
          appointmentStart={activeNote.sessionsAppointmentStart || owner?.when || null}
          appointmentStatus={statusFor(owner)}
          sections={activeNote.sections || {}}
          notepadContentR={activeNote.notepadContent || ""}
          notepadContentType={activeNote.notepadContentType || ""}
          handwritingImageR={activeNote.handwritingImage || null}
          mediaFilesR={activeNote.mediaFiles || []}
          templateId={activeNote.sessionsTemplateId}
          practiceId={activeNote.sessionsPracticeId || owner?.practiceId || practiceId}
          resourceId={activeNote.sessionsResourceId || owner?.resourceId || null}
          capturedValuesR={activeNote.capturedValues || {}}
          sessionsTemplateVersionR={activeNote.sessionsTemplateVersion}
          updatedAtR={activeNote.updatedAt || null}
          sessionType={activeNoteKind || deriveKind(activeNote)}
          // Never the compatibility multi-tab view: the list already offers a
          // row per kind, and saving writes the whole document back, so the
          // kinds not on screen are preserved.
          legacyBundle={false}
          canInvoice={canInvoiceFor(owner)}
          onAppointmentCompleted={markAppointmentCompletedFor(owner)}
          alreadyInvoiced={!!owner?.isInvoiced}
          serviceId={owner?.isCurrent ? appointment?.serviceId : undefined}
          onSaved={(id) => load(id || activeNote._id, { reopen: false })}
        />
      );
    }
    const hasAny = sittings.some((s) => !s.isGhost && s.notes.length > 0);
    return (
      <div className="acc-notes-editor d-flex align-items-center justify-content-center text-center text-muted p-4">
        {loading && rows.length === 0 ? (
          <div>
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 mb-0">Loading notes…</p>
          </div>
        ) : loadError && rows.length === 0 ? (
          <div>
            <i className="fa-solid fa-circle-info d-block mb-2 text-primary" style={{ fontSize: "1.75rem" }} />
            <p className="mb-2">The notes could not be loaded.</p>
            <Button variant="outline-primary" size="sm" style={{ minWidth: 120 }} onClick={() => load()}>
              <i className="fa-solid fa-rotate me-2" />
              Try again
            </Button>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <i className="fa-light fa-note-medical" style={{ fontSize: "1.5rem" }} />
            <p className="mb-0">
              {hasAny
                ? "Select a note to read it, or use Add note to start one."
                : pinned
                  ? "No notes on this appointment yet. Use Add note to start one."
                  : "No notes for this patient yet. Use Ad hoc note to start one."}
            </p>
          </div>
        )}
      </div>
    );
  };

  const showContext = !!pinned && !!appointment;

  return (
    <Container fluid className="pt-2 acc-list">
      <AppToast show={toast.show} onClose={hideToast} variant={toast.variant} message={toast.message} />
      <ConfirmModal {...confirmProps} />
      <NoteAuditModal
        show={!!auditFor}
        onHide={() => setAuditFor(null)}
        sessionId={auditFor?.id || null}
        noteTitle={auditFor?.title || ""}
      />

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <h5 className="text-primary mb-0">Notes</h5>
        {/* What has been recorded and not written up, and what was thrown
            away. Patient-wide, so they sit at section level rather than on
            any one sitting. */}
        {syncToOn && (
          <div className="d-flex align-items-center gap-2">
            <span className="text-primary fw-semibold small">SyncTo:</span>
            <CaptureReviewBar
              patientId={patientId}
              onChanged={() => load(activeNote?._id, { reopen: false })}
            />
          </div>
        )}
      </div>

      {showContext && (
        <div className="posted-invoice-info acc-notes-context mb-2">
          <i className="fa-solid fa-circle-info text-primary" />
          <span className="fw-semibold text-primary">{formatWhen(appointment.start)}</span>
          {service?.servicesName && <span>{service.servicesName}</span>}
          {appointment.status && (
            <Badge bg={appointment.status === "Completed" ? "success" : "secondary"}>
              {appointment.status}
            </Badge>
          )}
          {pinned.bookingNote && (
            <span className="acc-notes-context-booking">
              <span className="fw-semibold text-primary">Booking note: </span>
              {pinned.bookingNote}
            </span>
          )}
        </div>
      )}

      <Row className="g-3">
        <Col xs={12} lg={4} xl={3}>
          <div className="acc-notes-list">
            {loadError && rows.length > 0 && (
              <div className="small text-danger px-1">
                The list could not be refreshed.{" "}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => load(undefined, { reopen: false })}>
                  Try again
                </Button>
              </div>
            )}
            {sittings.map(renderSitting)}
            {loading && rows.length === 0 && (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" variant="primary" />
              </div>
            )}
            {registered && serverSkip < total && (
              <Button
                variant="link"
                size="sm"
                className="text-primary text-decoration-none align-self-start"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? <Spinner size="sm" animation="border" /> : "Load more"}
              </Button>
            )}
          </div>
        </Col>
        <Col xs={12} lg={8} xl={9} ref={editorColRef} className="acc-notes-editor-col">
          {draftNoteKind || activeNote ? (
            <div className="acc-notes-editor is-active">{renderEditor()}</div>
          ) : (
            renderEditor()
          )}
        </Col>
      </Row>
    </Container>
  );
}
